import { useState, useCallback, useContext } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Typography,
  Box,
  IconButton,
  Dialog,
  Link,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useDropzone } from "react-dropzone";

import { appLogger, Http } from "../../../utils";
import { UserContext } from "../../../context/userContext";
import { UserLoginInfo } from "../../../types/context";

import { useParams } from "react-router-dom";

import uuid from "react-uuid";

interface IProps {
  open: boolean;
  onClose: () => void;
  handleFetchStoredDocuments: () => void;
  setFileUploading: (value: any) => void;
  selectedFolder: any;
  handleGetS3Url: any;
  isRetrain?: boolean;
}

const UploadDocument = ({
  open,
  onClose,
  setFileUploading,
  handleFetchStoredDocuments,
  selectedFolder,
  handleGetS3Url,
  isRetrain = false,
}: IProps) => {
  const { productId, versionId } = useParams();

  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  const [file, setFile] = useState<File | null>(null);

  const handleTrainKnowledgebase = async (values: any, fileName: string) => {
    try {
      const res: any = await Http.post({
        url: `/api/v2/kbArticles/train`,
        state: userInfo,
        body: {
          productId: productId,
          productVersion: versionId,
          folderName: isRetrain ? selectedFolder.folder : selectedFolder.id,
          fileName,
          fileType: values.type,
          userFileName: values.name,
          oldId: isRetrain ? selectedFolder.id : "",
        },
      });

      handleFetchStoredDocuments();
    } catch (err) {
      appLogger.error({
        infoObj: {
          method: "UploadDocument",
          component: "Knowledgebase",
          fileName: "UploadDocument.tsx",
        },
      });
      setFileUploading({ uploading: false, name: "", percentage: 0 });
    }
  };

  const handleUpload = async () => {
    onClose();

    appLogger.info({
      infoObj: {
        method: "handleUpload",
        component: "Knowledgebase",
        fileName: "UploadDocument.tsx",
      },
    });

    setFileUploading({ uploading: true, name: file?.name, percentage: 0 });
    if (file) {
      const formData = new FormData();
      const res: any = await handleGetS3Url(file, isRetrain, selectedFolder);

      if (res) {
        const { data, fileName } = res;

        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          const percentage = +((event.loaded / event.total) * 100).toFixed(2);
          setFileUploading((prevState: any) => {
            return { ...prevState, percentage };
          });
        };
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200 || xhr.status === 204) {
              setFileUploading({ uploading: false, name: "", percentage: 0 });
              handleTrainKnowledgebase(file, fileName);
            } else {
              setFileUploading({ uploading: false, name: "", percentage: 0 });
              return;
            }
          }
        };
        Object.keys(data.fields).forEach((key) => {
          formData.append(key, data.fields[key]);
        });
        formData.append("file", file);
        xhr.open("POST", data.url, true);
        xhr.send(formData);
      }
    }
  };

  const onDrop = useCallback((acceptedFiles: any) => {
    const tempFile = acceptedFiles[0];

    setFile(tempFile);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/pdf": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/csv": [],
    },
  });

  const handleDownloadPDF = async () => {
    const pdfURL =
      "https://dev-cuvo-upload-photos.s3.amazonaws.com/sample_kb_pdf_format.pdf";

    // Replace 'your-s3-bucket-url/your-pdf-file.pdf' with the actual S3 bucket URL and PDF file path

    // Construct the full URL

    try {
      // Fetch the PDF file
      const response = await fetch(pdfURL);

      // Check if the request was successful (status code 200)
      if (response.ok) {
        // Convert the response to a blob
        const blob = await response.blob();

        // Create a virtual link element
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        // Add the 'download' attribute to force download
        link.download = "downloaded-file.pdf";

        // Append the link to the body
        document.body.appendChild(link);

        // Trigger a click on the link to start the download
        link.click();

        // Remove the link from the DOM
        document.body.removeChild(link);
      } else {
        appLogger.error({
          errObj: {
            method: "handleDownloadPDF",
            component: "UploadDocument",
            fileName: "UploadDocument.tsx",
            err: "Failed to download file",
          },
        });
      }
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "handleDownloadPDF",
          component: "UploadDocument",
          fileName: "UploadDocument.tsx",
          err: error,
        },
      });
    }
  };

  const handleDownloadExcel = async () => {
    // Replace 'your-xlsx-file-url.xlsx' with the actual URL of your XLSX file
    const xlsxFileUrl =
      "https://dev-cuvo-upload-photos.s3.amazonaws.com/sample_kb_pdf_format.xlsx";

    try {
      // Fetch the XLSX file
      const response = await fetch(xlsxFileUrl);

      // Check if the request was successful (status code 200)
      if (response.ok) {
        // Convert the response to a blob
        const blob = await response.blob();

        // Create a virtual link element
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);

        // Add the 'download' attribute to force download
        link.download = "downloaded-file.xlsx";

        // Append the link to the body
        document.body.appendChild(link);

        // Trigger a click on the link to start the download
        link.click();

        // Remove the link from the DOM
        document.body.removeChild(link);
      } else {
        appLogger.error({
          errObj: {
            method: "handleDownloadExcel",
            component: "UploadDocument",
            fileName: "UploadDocument.tsx",
            err: "Failed to download file",
          },
        });
      }
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "handleDownloadExcel",
          component: "UploadDocument",
          fileName: "UploadDocument.tsx",
          err: error,
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <Grid
        container
        justifyContent="end"
        sx={{
          padding: "12px 10px",
        }}
      >
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Grid>
      <Grid
        container
        justifyContent="center"
        sx={{
          padding: "12px 10px",
        }}
      >
        <Typography
          sx={{
            color: "#111d3b",
            fontSize: "25px",
            fontWeight: 500,
          }}
        >
          Upload from document
        </Typography>
      </Grid>

      <Grid container justifyContent="center">
        <Box>
          <Box display="flex">
            <Typography
              sx={{
                fontSize: "12px",
              }}
            >
              (Please refer to sample files on how to format kb)
            </Typography>
          </Box>

          <Box display="flex" gap="15px" justifyContent="center">
            <Link onClick={handleDownloadPDF}>sample pdf</Link>

            <Link
              href="https://dev-cuvo-upload-photos.s3.amazonaws.com/sample_kb_pdf_format.csv"
              target="_blank"
            >
              sample csv
            </Link>

            <Link onClick={handleDownloadExcel}>sample xlsx</Link>
          </Box>
        </Box>
      </Grid>

      <Grid
        container
        p={3}
        sx={{
          width: "700px",
          maxWidth: "700px",
          margin: "0 auto",
          borderRadius: "14px",
        }}
      >
        <Grid item xs={12} mt={2}>
          {file && (
            <Box>
              <Typography>Uploaded</Typography>
              <Box
                sx={{
                  border: "1px dashed #3C404333",
                  padding: "15px",
                  display: "inline-flex",
                  borderRadius: "8px",
                  marginTop: "10px",
                  flexBasis: "50%",
                  alignItems: "center",
                }}
              >
                <Typography>{file?.name}</Typography>
                <IconButton
                  sx={{
                    marginLeft: "auto",
                  }}
                  onClick={() => setFile(null)}
                >
                  <CloseIcon
                    sx={{
                      fontSize: "16px",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}

          {!file && (
            <>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Drag 'n' drop a file here, or click to select file
              </Typography>
              <Typography>
                <span className="title-tooltip">
                  (Only .csv, .xlsx and .pdf files will be accepted)
                </span>
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  background: "#E4E5E780",
                  border: "1px dashed #3C404333",
                  padding: "20px",
                  borderRadius: "8px",
                  marginTop: "10px",
                  cursor: "pointer",
                }}
              >
                <input {...getInputProps()} />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Knowledgebase file
                </Typography>
              </Box>
            </>
          )}
        </Grid>

        <Grid
          container
          alignItems="center"
          columnGap="15px"
          item
          xs={12}
          mt={2}
          justifyContent="end"
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              handleUpload();
            }}
            disabled={file === null}
          >
            {isRetrain ? "Retrain" : "Upload"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default UploadDocument;
